import React from 'react';
import { Button, Grid, Tooltip } from '@mui/material';
import { createClasses } from '@kp/react-ui';
import { useTranslation } from 'react-i18next';
import * as Icons from '@mui/icons-material';
import { Link } from '../../../components/Link';
import { entityLocation } from '../../../utils/entity';
import * as Entities from '../../../constants/Entities';
import {
  DataTable,
  DataTableHeader,
  DataTableRow,
} from '../../../components/DataTable';
import { EmptyBanner } from '../../../components/EmptyBanner';
import { ConditionalLoading } from '../../../components/ConditionalLoading';

const classes = createClasses({
  tooltipContent: {
    padding: '8px',
    fontSize: '14px',
    lineHeight: '16px',
  },
});

export type DeviceModelDetailAttributeSet = {
  id: string;
  name: string;
  attributesCount: number;
};

type DeviceModelDetailsAttributeSetsProps = {
  attributeSets: Array<DeviceModelDetailAttributeSet>;
  deviceModelId: string;
  isDeviceModelGlobal: boolean;
  loading: boolean;
  onAdd?: () => void;
};

export const DeviceModelDetailsAttributeSets: React.FC<
  DeviceModelDetailsAttributeSetsProps
> = ({ attributeSets, deviceModelId, isDeviceModelGlobal, loading, onAdd }) => {
  const { t } = useTranslation(['deviceModels', 'general']);

  const headers: DataTableHeader[] = [
    {
      key: t('deviceModels:details.attributeSets.headers.name'),
      value: t('deviceModels:details.attributeSets.headers.name'),
    },
    {
      key: t('deviceModels:details.attributeSets.headers.attributes'),
      value: t('deviceModels:details.attributeSets.headers.attributes'),
      align: 'right',
    },
  ];

  const rows: DataTableRow[] = attributeSets.map((attributeSet) => {
    return {
      key: `${attributeSet.id}`,
      cells: [
        {
          key: `${attributeSet.id}-cell-name`,
          element: (
            <Link
              to={entityLocation(
                Entities.DEVICEMODEL,
                `${deviceModelId}/attribute-sets/${attributeSet.id}`,
              )}
            >
              {attributeSet.name}
            </Link>
          ),
        },
        {
          key: `${attributeSet.id}-cell-range`,
          element: attributeSet.attributesCount,
          align: 'right',
        },
      ],
    };
  });

  return (
    <Grid container spacing={3} data-testid="attributeSetsTable">
      <Grid item xs={12} sm={12}>
        <ConditionalLoading loading={loading}>
          {attributeSets.length === 0 ? (
            <EmptyBanner
              description={t('deviceModels:details.attributeSets.empty')}
              actionLabel={t('deviceModels:details.attributeSets.add')}
              onClickAction={onAdd}
              disableAction={!onAdd || isDeviceModelGlobal}
            />
          ) : (
            <DataTable
              actions={
                <Tooltip
                  arrow
                  placement="top-end"
                  title={
                    isDeviceModelGlobal ? (
                      <div className={classes.tooltipContent}>
                        {t('deviceModels:details.attributeSets.globalWarning')}
                      </div>
                    ) : (
                      ''
                    )
                  }
                  aria-label="add-attribute-set-tooltip"
                >
                  <span>
                    <Button
                      color="secondary"
                      variant="outlined"
                      size="large"
                      startIcon={<Icons.Add />}
                      onClick={onAdd}
                      data-testid="add-attribute-set-button"
                      disabled={!onAdd || isDeviceModelGlobal}
                    >
                      {t('deviceModels:details.attributeSets.add')}
                    </Button>
                  </span>
                </Tooltip>
              }
              title={t('deviceModels:details.attributeSets.title')}
              loading={loading}
              showPagination={false}
              headers={headers}
              rows={rows}
            />
          )}
        </ConditionalLoading>
      </Grid>
    </Grid>
  );
};
